'use client'

import { Button } from '@brew/ui/button'
import { ClerkFailed, ClerkLoaded } from '@clerk/nextjs'
import { usePathname } from 'next/navigation'
import * as React from 'react'
import { isClerkCriticalPathname } from '@/lib/auth/clerk-critical-routes'
import {
  reportBrowserError,
  submitDDEvent,
} from '@/lib/observability/datadog-rum'
import { useAuthRecoveryCopy } from './use-auth-recovery-copy'

const CLERK_LOAD_ERROR_CODES = new Set([
  'failed_to_load_clerk_js',
  'failed_to_load_clerk_ui',
])

const CLERK_LOAD_MESSAGE_PATTERN =
  /failed to (?:load clerk (?:js|ui)|download latest clerkjs)/i
const CHUNK_LOAD_MESSAGE_PATTERN =
  /chunkloaderror|loading (?:css )?chunk [^\s]+ failed/i
const CLERK_REFERENCE_PATTERN =
  /@clerk|clerk\.brew\.new|clerk(?:js|\s+ui)|ui(?:\.shared)?\.browser\.js/i
const CLERK_ASSET_PATTERN =
  /clerk\.brew\.new\/(?:npm\/|[^\s?#)]+\.js)|\/npm\/@clerk\/(?:clerk-js|ui)@|ui(?:\.shared)?\.browser\.js|[\w-]+_ui_[\w.-]+\.js/i

const ERROR_DETAIL_KEYS = [
  'code',
  'message',
  'name',
  'stack',
  'filename',
  'src',
] as const

/**
 * Clerk asset delivery can fail independently of an app deployment. The
 * attempt count lives in
 * sessionStorage because each retry is a full reload; when storage is
 * unavailable we skip auto-retry entirely rather than risk a reload loop.
 */
export const MAX_CLERK_AUTO_RETRIES = 2
const AUTO_RETRY_DELAYS_MS = [1000, 2500] as const
const AUTO_RETRY_COUNT_KEY = 'clerk-load-auto-retry-count'
const FAILURE_REPORTED_KEY = 'clerk-load-failure-reported'
const FAILURE_ROUTE_KEY = 'clerk-load-failure-route-class'
const FAILURE_KIND_KEY = 'clerk-load-failure-kind'
const FAILURE_DETECTION_KEY = 'clerk-load-failure-detection'
const OUTCOME_REPORTED_KEY = 'clerk-load-recovery-outcome'
const FAILURE_KINDS = [
  'clerk_js',
  'clerk_ui',
  'clerk_asset',
  'provider_status',
  'unknown',
] as const
type ClerkLoadFailureKind = (typeof FAILURE_KINDS)[number]
const FAILURE_DETECTIONS = [
  'react-boundary',
  'window-error',
  'unhandled-rejection',
  'provider-status',
] as const
type ClerkLoadFailureDetection = (typeof FAILURE_DETECTIONS)[number]
const pageRecovery: {
  outcomes: Set<string>
  failureRouteClass: 'critical' | 'content' | 'unknown' | null
  failureKind: ClerkLoadFailureKind | null
  failureDetection: ClerkLoadFailureDetection | 'unknown' | null
} = {
  outcomes: new Set(),
  failureRouteClass: null,
  failureKind: null,
  failureDetection: null,
}

/** Tests replace the page lifecycle without replacing the browser module. */
export function resetClerkRecoveryOutcomesForTests(): void {
  pageRecovery.outcomes.clear()
  pageRecovery.failureRouteClass = null
  pageRecovery.failureKind = null
  pageRecovery.failureDetection = null
}

type RecoveryOutcome =
  | 'failure_detected'
  | 'content_degraded'
  | 'retry_scheduled'
  | 'exhausted'
  | 'recovery_required'
  | 'manual_retry'
  | 'recovered'

/** Unknown storage is not evidence that two reloads actually happened. */
function telemetryRetryCount(): number | null {
  try {
    const raw = window.sessionStorage.getItem(AUTO_RETRY_COUNT_KEY)
    if (raw === null) {
      return 0
    }
    const count = Number(raw)
    return Number.isInteger(count) &&
      count >= 0 &&
      count <= MAX_CLERK_AUTO_RETRIES
      ? count
      : null
  } catch {
    return null
  }
}

/** Bounded lifecycle evidence, deduplicated across remounts and automatic reloads. */
function reportRecoveryOutcome(outcome: RecoveryOutcome): void {
  const routeClass = isClerkCriticalPathname(window.location.pathname)
    ? 'critical'
    : 'content'
  const retryCount = telemetryRetryCount()
  const key =
    outcome === 'recovered'
      ? `${OUTCOME_REPORTED_KEY}:recovered`
      : `${OUTCOME_REPORTED_KEY}:${outcome}:${routeClass}:${retryCount ?? 'unknown'}`
  if (pageRecovery.outcomes.has(key) || readSessionValue(key) === 'true') {
    return
  }
  pageRecovery.outcomes.add(key)
  writeSessionValue(key, 'true')
  const failureRoute = pageRecovery.failureRouteClass ?? readFailureRouteClass()
  submitDDEvent('auth.clerk_load_recovery', {
    feature: 'clerk-auth-load',
    stage: outcome === 'recovered' ? 'provider_loaded' : 'provider_bootstrap',
    outcome,
    route_class: routeClass,
    failure_route_class: failureRoute ?? 'unknown',
    failure_kind: pageRecovery.failureKind ?? readFailureKind() ?? 'unknown',
    failure_detection:
      pageRecovery.failureDetection ?? readFailureDetection() ?? 'unknown',
    retry_count: retryCount,
    retry_limit: MAX_CLERK_AUTO_RETRIES,
  })
}

function readSessionValue(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function readFailureKind(): ClerkLoadFailureKind | null {
  const stored = readSessionValue(FAILURE_KIND_KEY)
  return FAILURE_KINDS.find((kind) => kind === stored) ?? null
}

function readFailureRouteClass(): 'critical' | 'content' | null {
  const stored = readSessionValue(FAILURE_ROUTE_KEY)
  return stored === 'critical' || stored === 'content' ? stored : null
}

function readFailureDetection(): ClerkLoadFailureDetection | null {
  const stored = readSessionValue(FAILURE_DETECTION_KEY)
  return FAILURE_DETECTIONS.find((detection) => detection === stored) ?? null
}

/** Returns whether the value verifiably persisted. */
function writeSessionValue(key: string, value: string): boolean {
  try {
    window.sessionStorage.setItem(key, value)
    return window.sessionStorage.getItem(key) === value
  } catch {
    return false
  }
}

function readAutoRetryCount(): number {
  try {
    const raw = window.sessionStorage.getItem(AUTO_RETRY_COUNT_KEY)
    if (raw === null) {
      return 0
    }
    const parsed = Number.parseInt(raw, 10)
    return Number.isFinite(parsed) ? parsed : MAX_CLERK_AUTO_RETRIES
  } catch {
    // Unreadable storage means we cannot bound reloads; treat the retry
    // budget as spent so the user gets the manual recovery UI instead.
    return MAX_CLERK_AUTO_RETRIES
  }
}

function autoRetryDelayMs(attempt: number): number {
  return AUTO_RETRY_DELAYS_MS[
    Math.min(attempt, AUTO_RETRY_DELAYS_MS.length - 1)
  ]
}

/** Persist the attempt before reloading; false means do not reload. */
function markAutoRetryAttempt(attempt: number): boolean {
  return writeSessionValue(AUTO_RETRY_COUNT_KEY, String(attempt + 1))
}

export type ClerkLoadFailureBoundaryProps = {
  children: React.ReactNode
  /** Test seam; production defaults to a hard reload for a clean Clerk boot. */
  onRetry?: () => void
}

type ClerkLoadFailureBoundaryState =
  | { kind: 'idle' }
  | { kind: 'retrying'; error: unknown }
  | { kind: 'recovery'; error: unknown }
  | { kind: 'rethrow'; error: unknown }

export type ClerkLoadFailureStatusProps = {
  /** Test seam; production defaults to a hard reload for a clean Clerk boot. */
  onRetry?: () => void
}

type ClerkLoadFailureRecoveryProps = {
  onRetry: () => void
}

function collectErrorDetails(
  value: unknown,
  details: Array<string>,
  seen: Set<object>,
  depth: number
): void {
  if (depth > 3 || value === null || value === undefined) {
    return
  }
  if (typeof value === 'string') {
    details.push(value)
    return
  }
  if (typeof value !== 'object' || seen.has(value)) {
    return
  }

  seen.add(value)
  const record = value as Record<string, unknown>
  for (const key of ERROR_DETAIL_KEYS) {
    const detail = record[key]
    if (typeof detail === 'string') {
      details.push(detail)
    }
  }

  collectErrorDetails(record.cause, details, seen, depth + 1)
  collectErrorDetails(record.error, details, seen, depth + 1)
  collectErrorDetails(record.reason, details, seen, depth + 1)
}

/**
 * Identifies only Clerk bootstrap and Clerk-hosted UI chunk failures. Generic
 * Next.js ChunkLoadErrors deliberately fall through to the normal app error
 * boundary instead of being mislabeled as authentication failures.
 */
function clerkLoadFailureKind(error: unknown): ClerkLoadFailureKind | null {
  const details: Array<string> = []
  collectErrorDetails(error, details, new Set(), 0)
  const fingerprint = details.join(' ')

  const code = details.find((detail) => CLERK_LOAD_ERROR_CODES.has(detail))
  if (code) {
    return code === 'failed_to_load_clerk_ui' ? 'clerk_ui' : 'clerk_js'
  }
  if (CLERK_LOAD_MESSAGE_PATTERN.test(fingerprint)) {
    return /load clerk ui/i.test(fingerprint) ? 'clerk_ui' : 'clerk_js'
  }
  if (CLERK_ASSET_PATTERN.test(fingerprint)) {
    return 'clerk_asset'
  }

  return CHUNK_LOAD_MESSAGE_PATTERN.test(fingerprint) &&
    CLERK_REFERENCE_PATTERN.test(fingerprint)
    ? 'clerk_asset'
    : null
}

export function isClerkLoadFailure(error: unknown): boolean {
  return clerkLoadFailureKind(error) !== null
}

function retryCurrentPage(): void {
  window.location.reload()
}

/**
 * Report at most one Clerk load failure per browser session. Auto-retries
 * are full reloads, so an instance flag alone would re-report after each
 * attempt and inflate the RUM impact count.
 */
function reportClerkLoadFailureOnce(
  error: unknown,
  detection: ClerkLoadFailureDetection,
  isCritical = isClerkCriticalPathname(window.location.pathname)
): void {
  const hasReported = readSessionValue(FAILURE_REPORTED_KEY) === 'true'
  const currentRouteClass = isCritical ? 'critical' : 'content'
  pageRecovery.failureRouteClass ??= hasReported
    ? (readFailureRouteClass() ?? 'unknown')
    : currentRouteClass
  // Retain the first observed failure, including through provider-status
  // fallback after a full reload. Legacy sessions cannot prove that attribution.
  pageRecovery.failureKind ??= hasReported
    ? (readFailureKind() ?? 'unknown')
    : (clerkLoadFailureKind(error) ?? 'provider_status')
  pageRecovery.failureDetection ??= hasReported
    ? (readFailureDetection() ?? 'unknown')
    : detection
  if (!hasReported) {
    writeSessionValue(FAILURE_ROUTE_KEY, pageRecovery.failureRouteClass)
    writeSessionValue(FAILURE_KIND_KEY, pageRecovery.failureKind)
    writeSessionValue(FAILURE_DETECTION_KEY, pageRecovery.failureDetection)
  }
  reportRecoveryOutcome(
    detection === 'react-boundary' || isCritical
      ? 'failure_detected'
      : 'content_degraded'
  )
  if (hasReported) {
    return
  }
  writeSessionValue(FAILURE_REPORTED_KEY, 'true')
  reportBrowserError(error, {
    feature: 'clerk-auth-load',
    detection,
    recoverable: true,
  })
}

function ClerkLoadRetrying() {
  const t = useAuthRecoveryCopy()
  return (
    <section
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[2147483647] flex min-h-svh items-center justify-center bg-background p-6"
    >
      <p className="text-muted-foreground text-sm">{t('reconnecting')}</p>
    </section>
  )
}

function ClerkLoadFailureRecovery({ onRetry }: ClerkLoadFailureRecoveryProps) {
  const t = useAuthRecoveryCopy()
  React.useEffect(() => {
    const count = telemetryRetryCount()
    reportRecoveryOutcome(
      count !== null && count >= MAX_CLERK_AUTO_RETRIES
        ? 'exhausted'
        : 'recovery_required'
    )
  }, [])
  const retry = () => {
    reportRecoveryOutcome('manual_retry')
    onRetry()
  }
  return (
    <section
      role="alert"
      aria-live="assertive"
      className="fixed inset-0 z-[2147483647] flex min-h-svh items-center justify-center bg-background p-6"
    >
      <div className="flex w-full max-w-lg flex-col gap-6 rounded-lg border border-border bg-card p-8 shadow-2xl">
        <div className="flex flex-col gap-2">
          <p className="font-medium text-muted-foreground text-xs uppercase">
            {t('kicker')}
          </p>
          <h1 className="font-semibold text-2xl text-foreground tracking-tight">
            {t('title')}
          </h1>
          <p className="text-muted-foreground text-sm leading-6">
            {t('description')}
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button type="button" onClick={retry} className="w-full sm:w-auto">
            {t('retry')}
          </Button>
          <p className="text-muted-foreground text-xs leading-5">{t('hint')}</p>
        </div>
      </div>
    </section>
  )
}

/**
 * Client error boundary around the root ClerkProvider. Clerk's loader reports
 * initial script failures through provider status, but a lazily loaded Clerk
 * UI chunk can also reject or throw later. The window listeners cover that
 * second path while the error classifier prevents unrelated app errors from
 * being swallowed.
 */
export class ClerkLoadFailureBoundary extends React.Component<
  ClerkLoadFailureBoundaryProps,
  ClerkLoadFailureBoundaryState
> {
  private hasReportedFailure = false
  private retryTimer: number | null = null

  constructor(props: ClerkLoadFailureBoundaryProps) {
    super(props)
    this.state = { kind: 'idle' }
    this.handleWindowError = this.handleWindowError.bind(this)
    this.handleUnhandledRejection = this.handleUnhandledRejection.bind(this)
  }

  static getDerivedStateFromError(
    error: unknown
  ): ClerkLoadFailureBoundaryState {
    if (!isClerkLoadFailure(error)) {
      return { kind: 'rethrow', error }
    }
    if (readAutoRetryCount() < MAX_CLERK_AUTO_RETRIES) {
      return { kind: 'retrying', error }
    }
    return { kind: 'recovery', error }
  }

  componentDidMount(): void {
    window.addEventListener('error', this.handleWindowError, true)
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection)
  }

  componentDidCatch(error: unknown): void {
    if (this.state.kind === 'retrying' || this.state.kind === 'recovery') {
      this.handleClerkFailure(error, 'react-boundary')
    }
  }

  componentWillUnmount(): void {
    if (this.retryTimer !== null) {
      window.clearTimeout(this.retryTimer)
    }
    window.removeEventListener('error', this.handleWindowError, true)
    window.removeEventListener(
      'unhandledrejection',
      this.handleUnhandledRejection
    )
  }

  private reportOnce(
    error: unknown,
    detection: ClerkLoadFailureDetection
  ): void {
    if (this.hasReportedFailure) {
      return
    }
    this.hasReportedFailure = true
    reportClerkLoadFailureOnce(error, detection)
  }

  private handleClerkFailure(
    error: unknown,
    detection: ClerkLoadFailureDetection
  ): void {
    if (!isClerkLoadFailure(error)) {
      return
    }
    this.reportOnce(error, detection)
    // Content-first routes (blog, pricing, browse, …) render fine without
    // Clerk, so a failed clerk.brew.new load must never cover the page or
    // force a reload there — report it and leave the content alone. The
    // react-boundary path is exempt: React has already unmounted the tree,
    // so recovery UI is the only option regardless of route.
    if (
      detection !== 'react-boundary' &&
      !isClerkCriticalPathname(window.location.pathname)
    ) {
      return
    }
    const attempt = readAutoRetryCount()
    if (attempt < MAX_CLERK_AUTO_RETRIES) {
      this.setState({ kind: 'retrying', error })
      this.scheduleAutoRetry(attempt, error)
    } else {
      this.setState({ kind: 'recovery', error })
    }
  }

  private scheduleAutoRetry(attempt: number, error: unknown): void {
    if (this.retryTimer !== null) {
      return
    }
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null
      if (!markAutoRetryAttempt(attempt)) {
        // Storage refused the write; without a persisted bound another
        // reload could loop, so surface the manual recovery UI instead.
        this.setState({ kind: 'recovery', error })
        return
      }
      ;(this.props.onRetry ?? retryCurrentPage)()
    }, autoRetryDelayMs(attempt))
    reportRecoveryOutcome('retry_scheduled')
  }

  private handleWindowError(event: Event): void {
    if (event instanceof ErrorEvent) {
      const scriptSrc =
        event.target instanceof HTMLScriptElement ? event.target.src : ''
      const error = new Error(
        [event.message, event.filename, scriptSrc].filter(Boolean).join(' '),
        { cause: event.error }
      )
      this.handleClerkFailure(error, 'window-error')
      return
    }

    if (event.target instanceof HTMLScriptElement) {
      this.handleClerkFailure(
        new Error(`Failed to load script ${event.target.src}`),
        'window-error'
      )
    }
  }

  private handleUnhandledRejection(event: PromiseRejectionEvent): void {
    this.handleClerkFailure(event.reason, 'unhandled-rejection')
  }

  render(): React.ReactNode {
    if (this.state.kind === 'idle') {
      return this.props.children
    }
    if (this.state.kind === 'rethrow') {
      throw this.state.error
    }
    if (this.state.kind === 'retrying') {
      return <ClerkLoadRetrying />
    }

    return (
      <ClerkLoadFailureRecovery
        onRetry={this.props.onRetry ?? retryCurrentPage}
      />
    )
  }
}

function ClerkProviderStatusReporter({ onRetry }: ClerkLoadFailureStatusProps) {
  const retry = onRetry ?? retryCurrentPage
  const pathname = usePathname()
  // Content-first routes (blog, pricing, browse, …) render fine without
  // Clerk: report the failure but never cover the page or auto-reload.
  // `usePathname` keeps this live — if the user then client-navigates to a
  // Clerk-critical route (e.g. /sign-in), the blocking recovery flow takes
  // over there.
  const isCritical = isClerkCriticalPathname(pathname ?? '/')
  const [attempt] = React.useState(readAutoRetryCount)
  const [hasExhaustedRetries, setHasExhaustedRetries] = React.useState(
    attempt >= MAX_CLERK_AUTO_RETRIES
  )

  // Recovery can mount immediately when storage is unreadable or retries are
  // spent. Commit attribution before that child's passive reporting effect.
  React.useLayoutEffect(() => {
    reportClerkLoadFailureOnce(
      new Error('ClerkProvider entered the error state while loading'),
      'provider-status',
      isCritical
    )
  }, [isCritical])

  React.useEffect(() => {
    if (!isCritical || attempt >= MAX_CLERK_AUTO_RETRIES) {
      return
    }
    const timer = window.setTimeout(() => {
      if (markAutoRetryAttempt(attempt)) {
        retry()
      } else {
        setHasExhaustedRetries(true)
      }
    }, autoRetryDelayMs(attempt))
    reportRecoveryOutcome('retry_scheduled')
    return () => window.clearTimeout(timer)
  }, [attempt, retry, isCritical])

  if (!isCritical) {
    return null
  }
  if (!hasExhaustedRetries) {
    return <ClerkLoadRetrying />
  }
  return <ClerkLoadFailureRecovery onRetry={retry} />
}

/**
 * Clerk 7 exposes initial JS/UI loader rejection as provider status rather
 * than throwing it through React. Mount this once inside ClerkProvider so
 * that path gets the same recovery UI as later chunk failures.
 */
export function ClerkLoadFailureStatus({
  onRetry,
}: ClerkLoadFailureStatusProps) {
  return (
    <>
      <ClerkLoaded>
        <ClerkLoadRecovered />
      </ClerkLoaded>
      <ClerkFailed>
        <ClerkProviderStatusReporter onRetry={onRetry} />
      </ClerkFailed>
    </>
  )
}

function ClerkLoadRecovered() {
  React.useEffect(() => {
    if (
      pageRecovery.failureRouteClass !== null ||
      readSessionValue(FAILURE_REPORTED_KEY) === 'true'
    ) {
      reportRecoveryOutcome('recovered')
    }
  }, [])
  return null
}
